import type { ApiEnvelope, ApiModel, ApiUser, CreatedPaymentOrder, HistoryPage, LedgerEntry, PaymentOrder, PaymentType, RegistrationResult, UsageEntry, WalletSnapshot } from './types';

const DEFAULT_API_BASE_URL = '/api';
let accountEpoch = 0;
let accountLookup = 0;

function announceAccount(userId: string | null): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('zhixin:account-change', { detail: { userId } }));
}

function announceOtherTabs(): void {
  try { localStorage.setItem('zhixin.account-change', String(Date.now())); } catch { /* Storage may be disabled. */ }
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
  }
}

function resolveBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  const trimmed = configured?.trim() ?? DEFAULT_API_BASE_URL;
  return (trimmed === '' ? DEFAULT_API_BASE_URL : trimmed).replace(/\/$/, '');
}

function unwrap<T>(payload: T | ApiEnvelope<T>): T {
  if (payload !== null && typeof payload === 'object' && 'data' in payload) return payload.data as T;
  return payload as T;
}

async function readPayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return undefined;
  try { return JSON.parse(text) as unknown; } catch { return text; }
}

function errorMessage(payload: unknown, status: number): string {
  if (typeof payload === 'string' && payload.trim() !== '') return payload;
  if (payload !== null && typeof payload === 'object') {
    const candidate = payload as ApiEnvelope<unknown>;
    if (candidate.error === 'REGISTRATION_DISABLED') return '当前已关闭新用户注册';
    if (candidate.error === 'REGISTRATION_UNAVAILABLE') return '注册服务暂时不可用，请稍后重试';
    if (candidate.error === 'INVALID_RESET_CODE') return '验证码无效或已过期，请重新获取';
    if (typeof candidate.message === 'string') return candidate.message;
    if (typeof candidate.error === 'string') return candidate.error;
    if (candidate.error !== null && typeof candidate.error === 'object') {
      const nested = candidate.error as { message?: unknown };
      if (typeof nested.message === 'string') return nested.message;
    }
  }
  if (status === 401) return '请先登录后继续';
  if (status === 403) return '当前账号没有权限执行此操作';
  return `请求失败（${status}）`;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> { body?: unknown; }

export class ApiClient {
  readonly baseUrl: string;

  constructor(baseUrl = resolveBaseUrl()) { this.baseUrl = baseUrl.replace(/\/$/, ''); }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const headers = new Headers(options.headers);
    if (options.body !== undefined) headers.set('content-type', 'application/json');
    const response = await fetch(`${this.baseUrl}${path}`, { ...options, credentials: 'include', headers, body: options.body === undefined ? undefined : JSON.stringify(options.body) });
    const payload = await readPayload(response);
    if (!response.ok) {
      const code = payload !== null && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string' ? payload.error : undefined;
      throw new ApiClientError(errorMessage(payload, response.status), response.status, code);
    }
    return unwrap(payload as T | ApiEnvelope<T>);
  }

  get<T>(path: string): Promise<T> { return this.request<T>(path); }
  post<T>(path: string, body: unknown): Promise<T> { return this.request<T>(path, { method: 'POST', body }); }
  register(email: string, password: string): Promise<RegistrationResult> { return this.post('/v1/auth/register', { email, password }); }
  verifyEmail(userId: string, code: string): Promise<{ ok: true }> { return this.post('/v1/auth/verify-email', { userId, code }); }
  requestPasswordReset(email: string): Promise<{ ok: true; message: string }> { return this.post('/v1/auth/password-reset/request', { email }); }
  confirmPasswordReset(email: string, code: string, password: string): Promise<{ ok: true }> { return this.post('/v1/auth/password-reset/confirm', { email, code, password }); }
  async login(email: string, password: string): Promise<{ user: ApiUser }> {
    const epoch = ++accountEpoch;
    const result = await this.post<{ user: ApiUser }>('/v1/auth/login', { email, password });
    if (epoch === accountEpoch) {
      ++accountEpoch;
      announceAccount(result.user.id);
      announceOtherTabs();
    }
    return result;
  }
  logout(): Promise<void> {
    const epoch = ++accountEpoch;
    return this.post('/v1/auth/logout', {}).then(() => {
      if (epoch === accountEpoch) {
        ++accountEpoch;
        announceAccount(null);
        announceOtherTabs();
      }
    });
  }
  async currentUser(): Promise<ApiUser> {
    const epoch = accountEpoch;
    const lookup = ++accountLookup;
    try {
      const user = (await this.get<{ user: ApiUser }>('/v1/me')).user;
      if (epoch === accountEpoch && lookup === accountLookup) announceAccount(user.status === 'disabled' ? null : user.id);
      return user;
    } catch (error) {
      if (epoch === accountEpoch && lookup === accountLookup && error instanceof ApiClientError && error.status === 401) announceAccount(null);
      throw error;
    }
  }
  async listModels(): Promise<ApiModel[]> { return (await this.get<{ models: ApiModel[] }>('/v1/models')).models; }
  wallet(): Promise<WalletSnapshot> { return this.get('/v1/wallet'); }
  async ledger(): Promise<LedgerEntry[]> { return (await this.get<{ entries: LedgerEntry[] }>('/v1/wallet/ledger')).entries; }
  ledgerPage(offset = 0, limit = 50, signal?: AbortSignal): Promise<HistoryPage & { entries: LedgerEntry[] }> {
    return this.request(`/v1/wallet/ledger?limit=${limit}&offset=${offset}`, { signal });
  }
  usagePage(offset = 0, limit = 50, signal?: AbortSignal): Promise<HistoryPage & { usage: UsageEntry[] }> {
    return this.request(`/v1/usage?limit=${limit}&offset=${offset}`, { signal });
  }
  createPaymentOrder(amount: number, paymentType?: PaymentType): Promise<CreatedPaymentOrder> {
    if (!Number.isFinite(amount) || amount <= 0) return Promise.reject(new ApiClientError('充值金额必须大于零', 400, 'INVALID_AMOUNT'));
    if (paymentType !== undefined && paymentType !== 'wxpay' && paymentType !== 'alipay') return Promise.reject(new ApiClientError('请选择微信支付或支付宝支付', 400, 'INVALID_PAYMENT_TYPE'));
    return this.post('/v1/payments/orders', { amount, ...(paymentType === undefined ? {} : { paymentType }) });
  }
  paymentOrder(id: string): Promise<PaymentOrder> { return this.get(`/v1/payments/orders/${encodeURIComponent(id)}`); }
}

export const apiClient = new ApiClient();
